import type { JSX } from 'react';
import { useState } from 'react';
import { Link } from 'react-router';

import { useGetPartnerWebhooksQuery, useTestPartnerWebhookMutation } from 'features/webhooks/services/webhooks.api';
import { Loader2, PlayCircle, Trash2, Zap } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { cn } from '@/lib/utils';

interface NotifyWebhookActionRowProps {
  actionIndex: number;
  alertIndex: number;
  projectId: number;
  selectedWebhookId: number | null;
  onWebhookChange: (webhookId: number | null) => void;
  onDelete: () => void;
}

const NotifyWebhookActionRow = ({
  actionIndex,
  alertIndex,
  projectId,
  selectedWebhookId,
  onWebhookChange,
  onDelete,
}: NotifyWebhookActionRowProps): JSX.Element => {
  const [testResult, setTestResult] = useState<{ success: boolean; message: string } | null>(null);
  const { data: webhooksList, isLoading: webhooksLoading } = useGetPartnerWebhooksQuery({ projectId, enabled: true });
  const [testWebhook, { isLoading: isTesting }] = useTestPartnerWebhookMutation();

  const webhooks = webhooksList?.data ?? [];
  const selectId = `alert-${alertIndex}-action-${actionIndex}-webhook`;

  const handleTest = async (): Promise<void> => {
    if (!selectedWebhookId) return;
    setTestResult(null);
    try {
      const result = await testWebhook({ webhookId: selectedWebhookId, projectId }).unwrap();
      setTestResult({
        success: result.success,
        message: result.success ? 'Enviado correctamente.' : result.error ?? 'Error al enviar.',
      });
    } catch {
      setTestResult({ success: false, message: 'Error al probar el webhook.' });
    }
  };

  return (
    <div className="rounded-lg border border-border-subtle bg-bg-surface-alt p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 rounded bg-primary/10 px-2 py-0.5 font-mono text-xs font-semibold text-primary">
            <Zap className="size-3" aria-hidden />
            notifyWebhook
          </span>
          <span className="inline-block rounded-full bg-amber-100 px-2.5 py-0.5 text-[11px] font-bold text-amber-700">
            Legacy
          </span>
        </div>
        <Button type="button" variant="ghost" size="icon-sm" onClick={onDelete} aria-label="Eliminar acción">
          <Trash2 className="size-4" aria-hidden />
        </Button>
      </div>
      <p className="text-xs text-amber-700 font-body">
        ⚠ Esta función <strong>ya no se ejecuta</strong>. Cámbiala por <strong>&ldquo;Enviar a webhooks&rdquo;</strong>{' '}
        para que la alerta llegue.
      </p>

      <div>
        <label htmlFor={selectId} className="mb-1 block text-sm font-medium text-text-muted font-body">
          Webhook destino
        </label>
        <div className="flex items-center gap-2">
          <Select
            value={selectedWebhookId?.toString() ?? ''}
            onValueChange={(val) => {
              setTestResult(null);
              onWebhookChange(val ? Number(val) : null);
            }}
            disabled={webhooksLoading}
          >
            <SelectTrigger id={selectId} className="flex-1 w-full">
              <SelectValue placeholder={webhooksLoading ? 'Cargando…' : 'Selecciona un webhook'} />
            </SelectTrigger>
            <SelectContent position="popper">
              {webhooks.map((wh) => (
                <SelectItem key={wh.id} value={wh.id.toString()}>
                  {wh.name}
                </SelectItem>
              ))}
              {webhooks.length === 0 && !webhooksLoading && (
                <div className="px-2 py-3 text-sm text-center text-text-muted">Sin webhooks activos</div>
              )}
            </SelectContent>
          </Select>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0 gap-1.5"
            onClick={() => {
              handleTest().catch(() => {});
            }}
            disabled={!selectedWebhookId || isTesting}
          >
            {isTesting ? (
              <Loader2 className="size-3.5 animate-spin" aria-hidden />
            ) : (
              <PlayCircle className="size-3.5" aria-hidden />
            )}
            Probar
          </Button>
        </div>

        {testResult !== null ? (
          <p className={cn('mt-1.5 text-xs font-body', testResult.success ? 'text-success' : 'text-error')}>
            {testResult.message}
          </p>
        ) : null}

        <p className="mt-1.5 text-xs text-text-muted font-body">
          ¿No encuentras el webhook?{' '}
          <Link to="../webhooks" className="text-primary underline-offset-2 hover:underline">
            Ir a Webhooks →
          </Link>
        </p>
      </div>
    </div>
  );
};

export default NotifyWebhookActionRow;
